import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import {
  ArrowInIcon,
  CreditCardIcon,
  DesignIcon,
  FinanceIcon,
  LegalIcon,
  MarketingIcon,
  NavArrowRightIcon,
  PersonalIcon,
  ProductIcon,
  RightArrowIcon,
  SalesIcon,
  SettingsIcon,
  WhiteboardTemplateIcon,
} from '@sketchnote/icons';
import { Input } from '@sketchnote/shared-components';
import * as React from 'react';
import { useTldrawApp } from 'sn-wb-tldraw';
import { API } from 'src/api';

import { TemplateCard } from '../TemplatesPanel/TemplateCard';

interface TemplateStoreProps {
  customTemplates: any[];
  categories: any[];
}

export const sidebar = [
  { name: 'Finance', icon: <FinanceIcon /> },
  { name: 'Design', icon: <DesignIcon /> },
  { name: 'Marketing', icon: <MarketingIcon /> },
  { name: 'Legal', icon: <LegalIcon /> },
  { name: 'Product ', icon: <ProductIcon /> },
  { name: 'Accounting', icon: <CreditCardIcon /> },
  { name: 'Sales', icon: <SalesIcon /> },
  { name: 'Personal', icon: <PersonalIcon /> },
];

export const TemplateStore = React.memo(function TemplateStore({
  customTemplates,
  categories,
}: TemplateStoreProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const app = useTldrawApp();
  const [openSnackbar] = useSnackbar();

  const [filteredTemplates, setFilteredTemplates] = React.useState<any>(customTemplates);

  const getCategoryName = (categoryId: string): string => {
    const category = categories?.find((category) => category._id === categoryId);

    return category?.title;
  };

  React.useEffect(() => {
    setFilteredTemplates(customTemplates);
  }, [customTemplates]);

  const deleteTemplate = (id) => {
    API.delete('/whiteboard/template?id=' + id).then(() => {
      const newTemplates = customTemplates.filter((temp) => temp._id !== id);
      setFilteredTemplates(newTemplates);
      openSnackbar({
        text: 'Template successfully deleted',
        snackType: 'standard',
        position: 'bottom-center',
      });
    });
  };

  const handleTemplateEdit = (template, values) => {
    app.storeCurrentTemplate();
    app.storeTemplateData(values);
    app.deleteAll();
    app.createTemplateAtPoint(template);
    app.setTemplateLibrary(false);
    app.setTemplateEditMode(true);
  };

  const handleFilter = (e) => {
    setFilteredTemplates(
      customTemplates.filter((temp) =>
        temp.title.toLowerCase().includes(e.target.value.toLowerCase())
      )
    );
  };

  const topics = React.useMemo(() => ['Product', 'Marketing', 'Financial'], []);
  return (
    <>
      <RightArrowIcon
        fill="#878A92"
        className="cursor-pointer transform -rotate-45"
        onClick={onOpen}
      />
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent style={{ height: '816px', maxWidth: '1150px', borderRadius: '4px' }}>
          <div
            style={{
              height: '60px',
              boxShadow: '0px 2px 12px rgba(19, 23, 32, 0.08)',
              borderBottom: '1px solid #E2E4E9',
            }}
            className="pl-24 flex gap-12 items-center"
          >
            <ModalCloseButton style={{ top: '12px' }} />
            <WhiteboardTemplateIcon fill="#000" />
            <h3 className="font-medium text-lg">Template Library</h3>
          </div>
          <div className="flex h-full ">
            <div
              className="w-256 bg-white"
              style={{
                borderRight: '1px solid #E2E4E9',
              }}
            >
              <div className="px-24 py-32">
                <h4 className="text-xs leading-18 text-gray-999 mb-8">Categories</h4>
                <ul
                  className="flex flex-col pb-10"
                  style={{
                    borderBottom: '1px solid #E2E4E9',
                  }}
                >
                  {sidebar.map((item) => (
                    <li key={item.name} className="flex items-center gap-8 py-10 px-12">
                      {item.icon}
                      <h3 className="text-sm font-regular leading-20 text-gray-600">{item.name}</h3>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex flex-1 h-full bg-gray-100 px-48 pt-32 overflow-auto">
              <div className="px-24 flex flex-col gap-24">
                <h1 className="font-bold text-2xl text-gray-999">
                  Start building with ready-to-go templates
                </h1>
                <Input
                  type="text"
                  placeholder="search for any templates"
                  inputClassName="h-48 py-14 pl-20 bg-white rounded-sm"
                  onChange={(e) => handleFilter(e)}
                />
                <div className="flex gap-8 items-center">
                  <h5 className="text-sm font-regular">Popular Topics:</h5>
                  {topics.map((topic) => (
                    <h5 key={topic} className="text-gray-600 text-sm font-medium">
                      {topic}
                    </h5>
                  ))}
                </div>
                <div className="flex flex-col gap-12">
                  <h3 className="font-medium text-lg">We recommend</h3>
                  <div
                    className="flex gap-16 flex-wrap"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                  >
                    {filteredTemplates.map((template) => (
                      <TemplateCard
                        key={template._id}
                        onClick={() => {
                          app.createTemplateAtPoint({
                            shapes: template.shapes,
                            bindings: template.bindings,
                            assets: template.assets,
                            sections: template.sections,
                          });
                          app.setTemplateLibrary(false);
                          app.zoomToFit();
                          openSnackbar({
                            text: `Template added`,
                            snackType: 'standard',
                            position: 'bottom-center',
                            duration: 1500,
                          });
                        }}
                        onEdit={handleTemplateEdit.bind(
                          null,
                          {
                            shapes: template.shapes,
                            bindings: template.bindings,
                            assets: template.assets,
                            sections: template.sections,
                          },
                          template
                        )}
                        title={template.title}
                        description={template.description}
                        image={{
                          url: template.coverImage,
                          alt: template.title,
                        }}
                        color={['blue', 'pink', 'green'][Math.floor(Math.random() * 3)]}
                        tag={getCategoryName(template.category)}
                        onDelete={() => deleteTemplate(template._id)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
});
